import { LocationSelector } from "@/components/LocationSelector";
import { ZoneSelector } from "@/components/ZoneSelector";
import Seo from "@/components/seo/Seo";
import { HelpRequestButton } from "@/components/HelpRequestButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useRegion } from "@/contexts/RegionContext";
import { useZone } from "@/contexts/ZoneContext";
import { toArray } from "@/lib/array";
import { normalizeImageUrl } from "@/lib/image-url";
import { sortMarketItemsPhotoFirst } from "@/lib/market-sort";
import {
  matchesPropertyMarketFilters,
  isPropertyFilterOptionAvailable,
  shouldShowPropertySubFilter,
} from "@/lib/property-market-filters";
import {
  searchCategories as allSearchCategories,
  type SearchCategory,
} from "@/lib/searchCategories";
import { cn } from "@/lib/utils";
import { type UnifiedMarketItem } from "@shared/schema";
import { australianStates, suburbsByState } from "@shared/suburbs";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  Clock,
  CarFront,
  Eye,
  Filter,
  Grid3x3,
  Home,
  MapPin,
  Megaphone,
  Package,
  Plus,
  Search,
  ShoppingBag,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";

// 초기에는 거래 밀도를 높이기 위해 중고물품·차량·부동산을 한 피드에 집중한다.
const marketCategories: SearchCategory[] = [
  { id: "all", label: "전체", subCategories: [] },
  allSearchCategories.find((c) => c.id === "community")!,
  allSearchCategories.find((c) => c.id === "used-car")!,
  allSearchCategories.find((c) => c.id === "real-estate")!,
].filter(Boolean);

// 지역 필터 (Market과 동일)
const regionFilters = [
  { id: "ALL", label: "전체" },
  { id: "NSW", label: "시드니 (NSW)" },
  { id: "VIC", label: "멜번 (VIC)" },
  { id: "QLD", label: "브리즈번/케언즈 (QLD)" },
  { id: "SA", label: "아델레이드 (SA)" },
  { id: "WA", label: "퍼스 (WA)" },
  { id: "NT", label: "다윈 (NT)" },
  { id: "ACT", label: "캔버라 (ACT)" },
];

export default function Market() {
  const {
    selectedRegion,
    selectedSuburb,
    setSelectedRegion,
    setSelectedSuburb,
  } = useRegion();
  const { selectedZoneId, selectedZone } = useZone();
  const [, setLocation] = useLocation();
  const [selectedMainCategory, setSelectedMainCategory] =
    useState<SearchCategory | null>(marketCategories[0]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showAll, setShowAll] = useState<boolean>(false);
  const [failedImageIds, setFailedImageIds] = useState<Set<string>>(
    () => new Set(),
  );

  // 모바일 필터 토글 상태
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // 세부 필터 관련 상태
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string | string[]>
  >({});

  // 지역 필터 상태
  const [selectedState, setSelectedState] = useState<string>("ALL");
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  // 가격 필터 상태
  const [priceFilter, setPriceFilter] = useState<string>("all");
  const [customMinPrice, setCustomMinPrice] = useState<string>("");
  const [customMaxPrice, setCustomMaxPrice] = useState<string>("");
  const [appliedMinPrice, setAppliedMinPrice] = useState<number | undefined>(
    undefined
  );
  const [appliedMaxPrice, setAppliedMaxPrice] = useState<number | undefined>(
    undefined
  );

  // RegionContext의 selectedRegion과 동기화
  useEffect(() => {
    if (selectedRegion) {
      if (selectedRegion === "ALL") {
        setSelectedState("ALL");
      } else {
        setSelectedState(selectedRegion);
      }
    }
  }, [selectedRegion]);

  // selectedState 변경 시 RegionContext도 업데이트
  const handleStateChange = (state: string) => {
    setSelectedState(state);
    if (state !== "ALL") {
      setSelectedRegion(state as any);
    } else {
      setSelectedRegion("ALL" as any);
    }
  };

  // URL 쿼리 파라미터에서 카테고리 읽기
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const categoryParam = params.get("category");

    if (categoryParam) {
      const category = marketCategories.find((c) => c.id === categoryParam);
      if (category) {
        setSelectedMainCategory(category);
      }
    }
  }, []);

  // 카테고리 클릭 핸들러
  const handleCategoryClick = (category: SearchCategory) => {
    setSelectedMainCategory(category);
    setSelectedFilters({});
    setPriceFilter("all");
    setAppliedMinPrice(undefined);
    setAppliedMaxPrice(undefined);
    const params = new URLSearchParams(window.location.search);
    if (category.id === "all") params.delete("category");
    else params.set("category", category.id);
    window.history.replaceState(null, "", `${window.location.pathname}${params.size ? `?${params}` : ""}`);
  };

  const handleSubCategoryFilterChange = (filterId: string, value: string) => {
    setSelectedFilters((current) => {
      const next = { ...current };
      if (value === "all") delete next[filterId];
      else next[filterId] = value;

      if (filterId === "property-transaction") {
        delete next["property-listing-type"];
        delete next["property-room-type"];
        delete next["property-price-period"];
      } else if (filterId === "property-listing-type") {
        delete next["property-room-type"];
      }
      return next;
    });
  };

  // Build query URL with filters
  const queryUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.append("zone", selectedZoneId);
    params.append("scope", "local-market");
    params.append("limit", "200");

    // Use RegionContext for location filtering
    if (selectedRegion !== "ALL") params.append("state", selectedRegion);
    if (selectedSuburb) params.append("suburb", selectedSuburb);

    const kind = selectedMainCategory?.id === "community"
      ? "used"
      : selectedMainCategory?.id === "used-car"
      ? "car"
      : selectedMainCategory?.id === "real-estate"
      ? "property"
      : "all";
    params.append("kind", kind);

    if (searchTerm) params.append("searchTerm", searchTerm);

    const queryString = params.toString();
    return queryString
      ? `/api/unified-market?${queryString}`
      : "/api/unified-market";
  }, [selectedZoneId, selectedRegion, selectedSuburb, searchTerm, selectedMainCategory]);

  // Fetch unified market data
  const { data: rawItems, isLoading } = useQuery({
    queryKey: [queryUrl],
  });

  // Filter and sort items (with safe array conversion)
  const processedItems = useMemo(() => {
    // Safe array conversion - prevents ".sort() is not a function" errors
    const allItems = toArray<UnifiedMarketItem>(rawItems);

    let result = allItems;

    if (selectedMainCategory && selectedMainCategory.id !== "all") {
      switch (selectedMainCategory.id) {
        case "community":
          result = result.filter((item) => item.sourceType === "used");
          break;
        case "real-estate":
          result = result.filter((item) => item.sourceType === "property");
          break;
        case "used-car":
          result = result.filter((item) => item.sourceType === "car");
          break;
      }
    }

    // Then apply search term filter
    result = result.filter(
      (item) =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (selectedMainCategory?.id === "real-estate") {
      result = result.filter((item) =>
        matchesPropertyMarketFilters(item, selectedFilters),
      );
    }

    // 카테고리 필터링 (selectedFilters 적용)
    if (selectedMainCategory && selectedMainCategory.subCategories.length > 0) {
      selectedMainCategory.subCategories.forEach((subCategory) => {
        const filterValue = selectedFilters[subCategory.id];
        if (filterValue && filterValue !== "all") {
          // 동네마켓: item-category 필터
          if (subCategory.id === "item-category") {
            result = result.filter(
              (item) => item.type === "trade" && item.category === filterValue
            );
          }
          // 모임: meetup-category 필터
          else if (subCategory.id === "meetup-category") {
            result = result.filter((item) => item.subcategory === filterValue);
          }
          // 중고차: car-type 또는 fuel-type 필터
          else if (
            subCategory.id === "car-type" ||
            subCategory.id === "fuel-type"
          ) {
            result = result.filter((item) => item.category === filterValue);
          }
          // 알바/구인: work-type 또는 job-role 필터
          else if (
            subCategory.id === "work-type" ||
            subCategory.id === "job-role"
          ) {
            result = result.filter((item) => item.category === filterValue);
          }
          // 동네톡톡: talk-category 필터
          else if (subCategory.id === "talk-category") {
            result = result.filter((item) => item.category === filterValue);
          }
        }
      });
    }

    // 가격 필터링
    if (appliedMinPrice !== undefined || appliedMaxPrice !== undefined) {
      result = result.filter((item) => {
        if (
          item.priceCents === null ||
          item.priceCents === undefined
        ) {
          return false;
        }
        const priceInCents = item.priceCents;
        const minMatch =
          appliedMinPrice === undefined ||
          priceInCents >= appliedMinPrice * 100;
        const maxMatch =
          appliedMaxPrice === undefined ||
          priceInCents <= appliedMaxPrice * 100;
        return minMatch && maxMatch;
      });
    }

    return sortMarketItemsPhotoFirst(result, failedImageIds);
  }, [
    rawItems,
    searchTerm,
    selectedFilters,
    selectedMainCategory,
    appliedMinPrice,
    appliedMaxPrice,
    failedImageIds,
  ]);

  // 첫 화면부터 충분한 거래량이 느껴지도록 고밀도 그리드 60개를 우선 노출한다.
  const displayedItems = showAll ? processedItems : processedItems.slice(0, 60);

  const formatPrice = (priceCents: number | null | undefined) => {
    if (priceCents === null || priceCents === undefined) return null;
    if (priceCents === 0) return "무료나눔";
    if (priceCents === -1) return "가격협의";
    return `$${(priceCents / 100).toLocaleString()}`;
  };

  const itemHref = (item: UnifiedMarketItem) =>
    item.detailUrl || (item.type === "trade" ? `/market/${item.id}` : `/city-classifieds/${item.id}`);

  // 지역 표시 이름 매핑 (주요 도시 이름 우선)
  const getDisplayLocation = () => {
    if (selectedSuburb) {
      const suburbInfo = Object.values(suburbsByState)
        .flat()
        .find((s) => s.id === selectedSuburb);
      return suburbInfo?.nameKo || selectedSuburb;
    }

    const stateInfo = australianStates.find((s) => s.id === selectedRegion);
    if (!stateInfo || selectedRegion === "ALL") {
      return selectedZone.label;
    }

    // 주요 도시 이름 매핑
    const majorCityMap: Record<string, string> = {
      NSW: "시드니",
      VIC: "멜번",
      QLD: "브리즈번",
      SA: "아델레이드",
      WA: "퍼스",
      NT: "다윈",
      ACT: "캔버라",
      TAS: "호바트",
    };

    return majorCityMap[selectedRegion] || stateInfo.nameKo || "전체 지역";
  };

  const displayLocation = getDisplayLocation();
  const createHref = selectedMainCategory?.id === "real-estate"
    ? "/properties/new"
    : selectedMainCategory?.id === "used-car"
      ? "/cars/new"
      : "/market/register";

  // ItemCard Component (Simple list view - like News/CommunityTalk)
  const ItemCard = ({ item }: { item: UnifiedMarketItem }) => (
    <Link
      href={itemHref(item)}
      data-testid={`card-item-${item.id}`}
    >
      <div className="pb-4 border-b border-border/30 cursor-pointer transition-colors last:border-b-0">
        <div className="flex gap-4">
          {/* Thumbnail */}
          {item.images && item.images.length > 0 ? (
            <div className="flex-shrink-0">
              <img
                src={normalizeImageUrl(item.images[0]) || ""}
                alt={item.title}
                className="w-20 h-20 object-cover rounded-md"
                onError={(e) => {
                  e.currentTarget.src =
                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23e5e7eb" width="80" height="80"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="12" dy="45" dx="40" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>
          ) : (
            <div className="flex-shrink-0 w-20 h-20 bg-muted rounded-md flex items-center justify-center">
              {item.type === "trade" ? (
                <Package className="w-8 h-8 text-muted-foreground" />
              ) : (
                <Megaphone className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <h3 className="font-semibold text-base mb-1 line-clamp-2 text-foreground">
              {item.title}
            </h3>

            {/* Summary/Description */}
            {item.description && (
              <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                {item.description}
              </p>
            )}

            {/* Meta Info - Price, Location, Time, Views */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {/* Price (for trade items) */}
              {item.priceCents !== null && item.priceCents !== undefined && (
                <>
                  <span className="text-foreground font-semibold">
                    {formatPrice(item.priceCents)}
                  </span>
                  <span className="text-muted-foreground/60">·</span>
                </>
              )}
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDistanceToNow(new Date(item.createdAt), {
                  addSuffix: true,
                  locale: ko,
                })}
              </div>
              {(item.suburb || item.city) && (
                <>
                  <span className="text-muted-foreground/60">·</span>
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    <span>{item.suburb || item.city}</span>
                  </div>
                </>
              )}
              {item.views !== undefined &&
                item.views !== null &&
                item.views > 0 && (
                  <>
                    <span className="text-muted-foreground/60">·</span>
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      <span>{item.views}</span>
                    </div>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );

  // 사진과 가격을 먼저 읽을 수 있는 고밀도 마켓 카드
  const ItemCardGrid = ({ item }: { item: UnifiedMarketItem }) => (
    <Link
      href={itemHref(item)}
      className="block self-start"
      data-testid={`card-item-grid-${item.id}`}
    >
      <article className="group cursor-pointer">
        <div className="aspect-square w-full overflow-hidden rounded-md bg-muted">
          {item.images && item.images.length > 0 ? (
            <img
              src={normalizeImageUrl(item.images[0]) || ""}
              alt={item.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
              onError={(e) => {
                setFailedImageIds((current) => {
                  if (current.has(String(item.id))) return current;
                  const next = new Set(current);
                  next.add(String(item.id));
                  return next;
                });
                e.currentTarget.src =
                  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              {item.type === "trade" ? (
                <Package className="w-12 h-12 text-muted-foreground" />
              ) : (
                <Megaphone className="w-12 h-12 text-muted-foreground" />
              )}
            </div>
          )}
        </div>

        <div className="pt-1.5">
          <h3 className="line-clamp-1 text-xs font-normal leading-[18px] text-foreground">
            {item.title}
          </h3>

          {item.priceCents !== null && item.priceCents !== undefined && (
            <p className="text-xs font-semibold leading-[18px] text-foreground">
              {formatPrice(item.priceCents)}{item.pricePeriod ? ` / ${item.pricePeriod === "weekly" ? "주" : item.pricePeriod}` : ""}
            </p>
          )}

          <div className="line-clamp-1 text-[10px] leading-[15px] text-muted-foreground">
            {item.suburb || item.city || "호주"} · {formatDistanceToNow(new Date(item.createdAt), {
              addSuffix: true,
              locale: ko,
            })}
          </div>
        </div>
      </article>
    </Link>
  );

  // MeetupListItem Component (List view for meetups) - 샘플 이미지 스타일 적용
  const MeetupListItem = ({ item }: { item: UnifiedMarketItem }) => {
    const hasImage = item.images && item.images.length > 0;

    return (
      <Link href={`/city-classifieds/${item.id}`}>
        <Card
          className="p-4 hover-elevate cursor-pointer"
          data-testid={`list-item-${item.id}`}
        >
          <div className="flex gap-4">
            {/* Thumbnail - 왼쪽 */}
            <div className="flex-shrink-0">
              {hasImage ? (
                <img
                  src={item.images![0]}
                  alt={item.title}
                  className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-muted rounded-lg flex items-center justify-center">
                  <Megaphone className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Content - 오른쪽 */}
            <div className="flex-1 min-w-0">
              {/* Title */}
              <h3 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2 line-clamp-1">
                {item.title}
              </h3>

              {/* Description Preview */}
              {item.description && (
                <p className="text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                  {item.description}
                </p>
              )}

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span>{item.suburb || item.city}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  <span>{item.views || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>
                    {formatDistanceToNow(new Date(item.createdAt), {
                      addSuffix: true,
                      locale: ko,
                    })}
                  </span>
                </div>
                {item.category && (
                  <Badge variant="secondary" className="text-xs">
                    {item.category === "event" ? "모임" : item.category}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </Card>
      </Link>
    );
  };

  // 카테고리별 인기 검색어
  const getPopularKeywords = () => {
    const categoryId = selectedMainCategory?.id || "community";
    const keywordMap: Record<string, string[]> = {
      community: ["가전제품", "가구", "유아동", "의류", "도서", "스포츠"],
      "community-talk": [
        "맛집",
        "동네행사",
        "반려동물",
        "운동",
        "생활/편의",
        "동네친구",
      ],
      "real-estate": [
        "룸메이트",
        "렌트",
        "쉐어하우스",
        "매매",
        "여성전용",
        "단기",
      ],
      "used-car": ["SUV", "세단", "하치백", "왜건", "저연비", "하이브리드"],
      job: ["시급", "주급", "파트타임", "풀타임", "레스토랑", "카페"],
      meetup: ["운동", "동네친구", "아웃도어", "독서", "음악", "취미"],
    };
    return keywordMap[categoryId] || keywordMap["community"];
  };

  return (
    <>
      <Seo
        title="동네 마켓 — 중고물품·자동차·부동산 | OZKNEWS"
        description="내 동네의 중고물품, 자동차와 부동산을 사진 중심의 하나의 마켓에서 사고팔아 보세요."
        path="/market"
        keywords={["동네 마켓", "중고거래", "자동차", "부동산", "호주 한인 마켓"]}
      />

      <div className="min-h-screen bg-background">
        <section className="border-b border-border bg-background">
          <div className="mx-auto w-full max-w-[1536px] px-4 py-4 md:px-6 lg:px-8">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <form className="flex min-w-0 flex-1 gap-2" onSubmit={(event) => { event.preventDefault(); setSearchTerm(searchQuery.trim()); }}>
                <div className="relative flex-1"><Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} className="h-11 rounded-full pl-11" placeholder="무엇을 찾고 계세요? 예: 책상, 캠리, 시드니 렌트" /></div>
                <Button type="submit" className="h-11 rounded-full px-5">검색</Button>
              </form>
              <div className="flex shrink-0 items-center gap-2"><ZoneSelector compact showSuburbs={false} /><Link href={createHref}><Button className="rounded-full"><Plus className="mr-1 h-4 w-4" />판매하기</Button></Link></div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {marketCategories.map((category) => {
                const Icon = category.id === "community" ? ShoppingBag : category.id === "used-car" ? CarFront : category.id === "real-estate" ? Home : Grid3x3;
                return <Button key={category.id} type="button" size="sm" variant={selectedMainCategory?.id === category.id ? "default" : "ghost"} className="rounded-full" onClick={() => handleCategoryClick(category)}><Icon className="mr-1.5 h-4 w-4" />{category.label === "동네마켓" ? "중고물품" : category.label}</Button>;
              })}
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1536px] px-4 py-4 md:px-6 lg:px-8">
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-xl font-bold text-foreground">
                  {displayLocation} {selectedMainCategory?.id === "all" ? "전체 매물" : selectedMainCategory?.label || "전체 매물"}
                </h1>
                <p className="mt-1 text-xs text-muted-foreground">
                  가까운 차량·부동산을 우선하며, 중고물품은 초기 거래 밀도를 위해 호주 전체 매물도 함께 보여줍니다.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 lg:hidden">
              <div className="flex items-center gap-3">
                {/* Mobile Filter Toggle Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden"
                  data-testid="button-mobile-filter-toggle"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  필터
                </Button>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                {/* Sidebar - Filters (Desktop) - FilterSidebar 스타일 */}
                <aside className="hidden w-56 shrink-0 border-r border-border pr-5 lg:sticky lg:top-20 lg:block lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-foreground font-bold text-xs">필터</h2>
                    {(Object.keys(selectedFilters).length > 0 ||
                      priceFilter !== "all") && (
                      <button
                        onClick={() => {
                          setSelectedFilters({});
                          setPriceFilter("all");
                          setAppliedMinPrice(undefined);
                          setAppliedMaxPrice(undefined);
                          setCustomMinPrice("");
                          setCustomMaxPrice("");
                        }}
                        className="text-muted-foreground hover:text-[#60a6fa] text-xs transition-colors"
                        data-testid="button-reset-all-filters"
                      >
                        초기화
                      </button>
                    )}
                  </div>

                  {/* 필터 섹션들 */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="mb-2 text-xs font-semibold text-foreground">카테고리</h3>
                      <div className="space-y-1">
                        {marketCategories.map((category) => (
                          <button
                            key={`sidebar-${category.id}`}
                            type="button"
                            onClick={() => handleCategoryClick(category)}
                            className={cn(
                              "flex w-full items-center rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                              selectedMainCategory?.id === category.id
                                ? "bg-primary/10 font-semibold text-primary"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground",
                            )}
                          >
                            {category.label === "동네마켓" ? "중고물품" : category.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 지역 필터 - 주요 지역 */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        주요 지역
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {regionFilters.map((region) => {
                          const isSelected = selectedState === region.id;
                          return (
                            <button
                              key={region.id}
                              onClick={() => handleStateChange(region.id)}
                              className={cn(
                                "text-xs px-2 py-1 rounded transition-colors",
                                isSelected
                                  ? "bg-[#60a6fa] text-white"
                                  : "bg-muted text-foreground hover:bg-muted/80"
                              )}
                            >
                              {region.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* 지역변경 버튼 */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        지역 변경
                      </label>
                      <LocationSelector
                        variant="button"
                        open={locationDialogOpen}
                        onOpenChange={setLocationDialogOpen}
                      />
                    </div>

                    {/* 서브카테고리 필터 */}
                    {selectedMainCategory &&
                      selectedMainCategory.subCategories.length > 0 && (
                        <div>
                          <h3 className="text-foreground font-semibold text-xs mb-2">
                            {selectedMainCategory.label} 상세 필터
                          </h3>
                          {selectedMainCategory.subCategories.filter(
                            (subCategory) =>
                              shouldShowPropertySubFilter(
                                subCategory.id,
                                selectedFilters,
                              ),
                          ).map(
                            (subCategory) => (
                              <div key={subCategory.id} className="mb-4">
                                <h4 className="text-foreground font-semibold text-xs mb-2">
                                  {subCategory.label}
                                </h4>
                                {subCategory.type === "select" &&
                                  subCategory.options && (
                                    <>
                                      {/* 중고거래 카테고리와 모임 카테고리는 라디오 버튼으로 표시 */}
                                      {subCategory.id === "item-category" ||
                                      subCategory.id === "meetup-category" ? (
                                        <RadioGroup
                                          value={
                                            (selectedFilters[
                                              subCategory.id
                                            ] as string) || "all"
                                          }
                                          onValueChange={(value) =>
                                            handleSubCategoryFilterChange(
                                              subCategory.id,
                                              value,
                                            )
                                          }
                                          className="space-y-1.5"
                                          data-testid={`radio-${subCategory.id}`}
                                        >
                                          {!subCategory.options.some(
                                            (opt) => opt.id === "all"
                                          ) && (
                                            <div className="flex items-center space-x-2">
                                              <RadioGroupItem
                                                value="all"
                                                id={`${subCategory.id}-all`}
                                                className="border-foreground text-[#60a6fa] data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                                              />
                                              <Label
                                                htmlFor={`${subCategory.id}-all`}
                                                className="text-foreground text-xs font-normal cursor-pointer"
                                              >
                                                전체
                                              </Label>
                                            </div>
                                          )}
                                          {subCategory.options.map((option) => (
                                            <div
                                              key={option.id}
                                              className="flex items-center space-x-2"
                                            >
                                              <RadioGroupItem
                                                value={option.id}
                                                id={`${subCategory.id}-${option.id}`}
                                                className="border-foreground text-[#60a6fa] data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                                              />
                                              <Label
                                                htmlFor={`${subCategory.id}-${option.id}`}
                                                className="text-foreground text-xs font-normal cursor-pointer"
                                              >
                                                {option.label}
                                              </Label>
                                            </div>
                                          ))}
                                        </RadioGroup>
                                      ) : (
                                        <Select
                                          value={
                                            (selectedFilters[
                                              subCategory.id
                                            ] as string) || "all"
                                          }
                                          onValueChange={(value) =>
                                            handleSubCategoryFilterChange(
                                              subCategory.id,
                                              value,
                                            )
                                          }
                                        >
                                          <SelectTrigger
                                            data-testid={`select-${subCategory.id}`}
                                          >
                                            <SelectValue placeholder="전체" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {subCategory.options.filter((option) =>
                                              isPropertyFilterOptionAvailable(
                                                subCategory.id,
                                                option.id,
                                                selectedFilters,
                                              ),
                                            ).map(
                                              (option) => (
                                                <SelectItem
                                                  key={option.id}
                                                  value={option.id}
                                                >
                                                  {option.label}
                                                </SelectItem>
                                              )
                                            )}
                                          </SelectContent>
                                        </Select>
                                      )}
                                    </>
                                  )}
                                {subCategory.type === "multiselect" &&
                                  subCategory.options && (
                                    <div className="flex flex-wrap gap-1.5">
                                      {subCategory.options.map((option) => {
                                        const isSelected = (
                                          (selectedFilters[
                                            subCategory.id
                                          ] as string[]) || []
                                        ).includes(option.id);
                                        return (
                                          <button
                                            key={option.id}
                                            onClick={() => {
                                              const current =
                                                (selectedFilters[
                                                  subCategory.id
                                                ] as string[]) || [];
                                              const updated = isSelected
                                                ? current.filter(
                                                    (id) => id !== option.id
                                                  )
                                                : [...current, option.id];
                                              setSelectedFilters((prev) => ({
                                                ...prev,
                                                [subCategory.id]: updated,
                                              }));
                                            }}
                                            className={`text-xs px-2 py-1 rounded transition-colors ${
                                              isSelected
                                                ? "bg-[#60a6fa] text-white"
                                                : "bg-muted text-foreground hover:bg-muted/80"
                                            }`}
                                            data-testid={`badge-${subCategory.id}-${option.id}`}
                                          >
                                            {option.label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                              </div>
                            )
                          )}
                        </div>
                      )}

                    {/* 가격 필터 - 동네마켓만 */}
                    {selectedMainCategory?.id === "community" && (
                      <div>
                        <h3 className="text-foreground font-semibold text-xs mb-2">
                          가격
                        </h3>
                        <RadioGroup
                          value={priceFilter}
                          onValueChange={(value) => {
                            setPriceFilter(value);
                            if (value === "all") {
                              setAppliedMinPrice(undefined);
                              setAppliedMaxPrice(undefined);
                            } else if (value === "free") {
                              setAppliedMinPrice(0);
                              setAppliedMaxPrice(0);
                            } else if (value === "under10") {
                              setAppliedMinPrice(undefined);
                              setAppliedMaxPrice(10);
                            } else if (value === "under20") {
                              setAppliedMinPrice(undefined);
                              setAppliedMaxPrice(20);
                            } else if (value === "under50") {
                              setAppliedMinPrice(undefined);
                              setAppliedMaxPrice(50);
                            }
                            // Custom 선택 시에는 적용하기 버튼 누를 때까지 필터 적용 안 함
                          }}
                          className="space-y-1.5"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="all"
                              id="price-all"
                              className="border-foreground text-[#60a6fa] data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                            />
                            <Label
                              htmlFor="price-all"
                              className="text-foreground text-xs font-normal cursor-pointer"
                            >
                              전체
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="free"
                              id="price-free"
                              className="border-foreground text-[#60a6fa] data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                            />
                            <Label
                              htmlFor="price-free"
                              className="text-foreground text-xs font-normal cursor-pointer"
                            >
                              나눔
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="under10"
                              id="price-under10"
                              className="border-foreground text-[#60a6fa] data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                            />
                            <Label
                              htmlFor="price-under10"
                              className="text-foreground text-xs font-normal cursor-pointer"
                            >
                              10 AUD 이하
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="under20"
                              id="price-under20"
                              className="border-foreground text-[#60a6fa] data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                            />
                            <Label
                              htmlFor="price-under20"
                              className="text-foreground text-xs font-normal cursor-pointer"
                            >
                              20 AUD 이하
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="under50"
                              id="price-under50"
                              className="border-foreground text-[#60a6fa] data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                            />
                            <Label
                              htmlFor="price-under50"
                              className="text-foreground text-xs font-normal cursor-pointer"
                            >
                              50 AUD 이하
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="custom"
                              id="price-custom"
                              className="border-foreground text-[#60a6fa] data-[state=checked]:bg-[#60a6fa] data-[state=checked]:border-[#60a6fa]"
                            />
                            <Label
                              htmlFor="price-custom"
                              className="text-foreground text-xs font-normal cursor-pointer"
                            >
                              직접 선택
                            </Label>
                          </div>
                        </RadioGroup>

                        {/* Custom Price Range Inputs */}
                        {priceFilter === "custom" && (
                          <div className="space-y-2 pt-2 pl-6">
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                placeholder="최소"
                                value={customMinPrice}
                                onChange={(e) =>
                                  setCustomMinPrice(e.target.value)
                                }
                                className="h-8 text-sm"
                              />
                              <span className="text-sm">-</span>
                              <Input
                                type="number"
                                placeholder="최대"
                                value={customMaxPrice}
                                onChange={(e) =>
                                  setCustomMaxPrice(e.target.value)
                                }
                                className="h-8 text-sm"
                              />
                              <span className="text-xs text-muted-foreground">
                                AUD
                              </span>
                            </div>
                            <Button
                              size="sm"
                              className="w-full h-8"
                              onClick={() => {
                                const min = customMinPrice
                                  ? parseFloat(customMinPrice)
                                  : undefined;
                                const max = customMaxPrice
                                  ? parseFloat(customMaxPrice)
                                  : undefined;
                                setAppliedMinPrice(min);
                                setAppliedMaxPrice(max);
                              }}
                              data-testid="button-apply-custom-price"
                            >
                              적용하기
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </aside>

                {/* Mobile Filters */}
                {showFilters && (
                  <div className="lg:hidden col-span-1">
                    <Card>
                      <CardContent className="p-6 space-y-6">
                        <div className="flex items-center justify-between">
                          <h2 className="font-semibold text-lg">필터</h2>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setShowFilters(false)}
                            data-testid="button-close-mobile-filters"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* 서브카테고리 필터 */}
                        {selectedMainCategory &&
                          selectedMainCategory.subCategories.length > 0 && (
                            <div className="space-y-4 pt-4 border-t">
                              <h3 className="text-sm font-medium">
                                {selectedMainCategory.label} 상세 필터
                              </h3>
                              {selectedMainCategory.subCategories.filter(
                                (subCategory) =>
                                  shouldShowPropertySubFilter(
                                    subCategory.id,
                                    selectedFilters,
                                  ),
                              ).map(
                                (subCategory) => (
                                  <div key={subCategory.id}>
                                    <label className="text-sm font-medium mb-2 block">
                                      {subCategory.label}
                                    </label>
                                    {subCategory.type === "select" &&
                                      subCategory.options && (
                                        <>
                                          {/* 중고거래 카테고리와 모임 카테고리는 라디오 버튼으로 표시 */}
                                          {subCategory.id === "item-category" ||
                                          subCategory.id ===
                                            "meetup-category" ? (
                                            <RadioGroup
                                              value={
                                                (selectedFilters[
                                                  subCategory.id
                                                ] as string) || "all"
                                              }
                                              onValueChange={(value) =>
                                                handleSubCategoryFilterChange(
                                                  subCategory.id,
                                                  value,
                                                )
                                              }
                                              className="space-y-1"
                                              data-testid={`radio-${subCategory.id}-mobile`}
                                            >
                                              {!subCategory.options.some(
                                                (opt) => opt.id === "all"
                                              ) && (
                                                <div className="flex items-center space-x-2">
                                                  <RadioGroupItem
                                                    value="all"
                                                    id={`${subCategory.id}-all-mobile`}
                                                  />
                                                  <Label
                                                    htmlFor={`${subCategory.id}-all-mobile`}
                                                    className="cursor-pointer text-sm font-normal leading-tight"
                                                  >
                                                    전체
                                                  </Label>
                                                </div>
                                              )}
                                              {subCategory.options.map(
                                                (option) => (
                                                  <div
                                                    key={option.id}
                                                    className="flex items-center space-x-2"
                                                  >
                                                    <RadioGroupItem
                                                      value={option.id}
                                                      id={`${subCategory.id}-${option.id}-mobile`}
                                                    />
                                                    <Label
                                                      htmlFor={`${subCategory.id}-${option.id}-mobile`}
                                                      className="cursor-pointer text-sm font-normal leading-tight"
                                                    >
                                                      {option.label}
                                                    </Label>
                                                  </div>
                                                )
                                              )}
                                            </RadioGroup>
                                          ) : (
                                            <Select
                                              value={
                                                (selectedFilters[
                                                  subCategory.id
                                                ] as string) || "all"
                                              }
                                              onValueChange={(value) =>
                                                handleSubCategoryFilterChange(
                                                  subCategory.id,
                                                  value,
                                                )
                                              }
                                            >
                                              <SelectTrigger
                                                data-testid={`select-${subCategory.id}-mobile`}
                                              >
                                                <SelectValue placeholder="전체" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {subCategory.options.filter((option) =>
                                                  isPropertyFilterOptionAvailable(
                                                    subCategory.id,
                                                    option.id,
                                                    selectedFilters,
                                                  ),
                                                ).map(
                                                  (option) => (
                                                    <SelectItem
                                                      key={option.id}
                                                      value={option.id}
                                                    >
                                                      {option.label}
                                                    </SelectItem>
                                                  )
                                                )}
                                              </SelectContent>
                                            </Select>
                                          )}
                                        </>
                                      )}
                                    {subCategory.type === "multiselect" &&
                                      subCategory.options && (
                                        <div className="flex flex-wrap gap-2">
                                          {subCategory.options.map((option) => {
                                            const isSelected = (
                                              (selectedFilters[
                                                subCategory.id
                                              ] as string[]) || []
                                            ).includes(option.id);
                                            return (
                                              <Badge
                                                key={option.id}
                                                variant={
                                                  isSelected
                                                    ? "default"
                                                    : "outline"
                                                }
                                                className="cursor-pointer hover-elevate active-elevate-2"
                                                onClick={() => {
                                                  const current =
                                                    (selectedFilters[
                                                      subCategory.id
                                                    ] as string[]) || [];
                                                  const updated = isSelected
                                                    ? current.filter(
                                                        (id) => id !== option.id
                                                      )
                                                    : [...current, option.id];
                                                  setSelectedFilters(
                                                    (prev) => ({
                                                      ...prev,
                                                      [subCategory.id]: updated,
                                                    })
                                                  );
                                                }}
                                                data-testid={`badge-${subCategory.id}-${option.id}-mobile`}
                                              >
                                                {option.label}
                                              </Badge>
                                            );
                                          })}
                                        </div>
                                      )}
                                  </div>
                                )
                              )}
                            </div>
                          )}

                        {/* 가격 필터 - 동네마켓만 */}
                        {selectedMainCategory?.id === "community" && (
                          <div className="space-y-3 pt-4 border-t">
                            <h3 className="text-sm font-medium">가격</h3>
                            <RadioGroup
                              value={priceFilter}
                              onValueChange={(value) => {
                                setPriceFilter(value);
                                if (value === "all") {
                                  setAppliedMinPrice(undefined);
                                  setAppliedMaxPrice(undefined);
                                } else if (value === "free") {
                                  setAppliedMinPrice(0);
                                  setAppliedMaxPrice(0);
                                } else if (value === "under10") {
                                  setAppliedMinPrice(undefined);
                                  setAppliedMaxPrice(10);
                                } else if (value === "under20") {
                                  setAppliedMinPrice(undefined);
                                  setAppliedMaxPrice(20);
                                } else if (value === "under50") {
                                  setAppliedMinPrice(undefined);
                                  setAppliedMaxPrice(50);
                                }
                              }}
                              className="space-y-1"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="all"
                                  id="price-all-mobile"
                                />
                                <Label
                                  htmlFor="price-all-mobile"
                                  className="cursor-pointer text-sm font-normal leading-tight"
                                >
                                  전체
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="free"
                                  id="price-free-mobile"
                                />
                                <Label
                                  htmlFor="price-free-mobile"
                                  className="cursor-pointer text-sm font-normal leading-tight"
                                >
                                  나눔
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="under10"
                                  id="price-under10-mobile"
                                />
                                <Label
                                  htmlFor="price-under10-mobile"
                                  className="cursor-pointer text-sm font-normal leading-tight"
                                >
                                  10 AUD 이하
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="under20"
                                  id="price-under20-mobile"
                                />
                                <Label
                                  htmlFor="price-under20-mobile"
                                  className="cursor-pointer text-sm font-normal leading-tight"
                                >
                                  20 AUD 이하
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="under50"
                                  id="price-under50-mobile"
                                />
                                <Label
                                  htmlFor="price-under50-mobile"
                                  className="cursor-pointer text-sm font-normal leading-tight"
                                >
                                  50 AUD 이하
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="custom"
                                  id="price-custom-mobile"
                                />
                                <Label
                                  htmlFor="price-custom-mobile"
                                  className="cursor-pointer text-sm font-normal leading-tight"
                                >
                                  직접 선택
                                </Label>
                              </div>
                            </RadioGroup>

                            {priceFilter === "custom" && (
                              <div className="space-y-2 pt-2 pl-6">
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    placeholder="최소"
                                    value={customMinPrice}
                                    onChange={(e) =>
                                      setCustomMinPrice(e.target.value)
                                    }
                                    className="h-8 text-sm"
                                  />
                                  <span className="text-sm">-</span>
                                  <Input
                                    type="number"
                                    placeholder="최대"
                                    value={customMaxPrice}
                                    onChange={(e) =>
                                      setCustomMaxPrice(e.target.value)
                                    }
                                    className="h-8 text-sm"
                                  />
                                  <span className="text-xs text-muted-foreground">
                                    AUD
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  className="w-full h-8"
                                  onClick={() => {
                                    const min = customMinPrice
                                      ? parseFloat(customMinPrice)
                                      : undefined;
                                    const max = customMaxPrice
                                      ? parseFloat(customMaxPrice)
                                      : undefined;
                                    setAppliedMinPrice(min);
                                    setAppliedMaxPrice(max);
                                  }}
                                >
                                  적용하기
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Main Content */}
                <main className="min-w-0 flex-1">
                  {/* Results Count */}
                  <div className="mb-4 flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {displayedItems.length > 0
                        ? `${displayedItems.length}개 표시 중`
                        : "검색 결과 없음"}
                    </p>
                    <span className="text-xs text-muted-foreground">사진 있는 상품 우선</span>
                  </div>

                  {/* Simple image grid */}
                  {isLoading ? (
                    <div className="grid w-full grid-cols-2 items-start gap-x-3 gap-y-6 sm:grid-cols-3 xl:grid-cols-4">
                      {[...Array(12)].map((_, i) => (
                        <div key={i}>
                          <Skeleton className="aspect-square w-full rounded-md" />
                          <Skeleton className="mt-2 h-4 w-4/5" />
                          <Skeleton className="mt-1 h-4 w-2/5" />
                        </div>
                      ))}
                    </div>
                  ) : displayedItems.length > 0 ? (
                    <div className="grid w-full grid-cols-2 items-start gap-x-3 gap-y-6 sm:grid-cols-3 xl:grid-cols-4">
                      {displayedItems.map((item) => (
                        <ItemCardGrid key={item.id} item={item} />
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-muted-foreground mb-6">
                        조건에 맞는 항목이 없습니다
                      </p>
                      <div className="flex justify-center">
                        <HelpRequestButton
                          sourceType="market"
                          sourceId={searchQuery || undefined}
                          zoneId={selectedRegion !== "ALL" ? selectedRegion : undefined}
                          suburb={selectedSuburb || undefined}
                          label="찾는 물건, 카톡으로 물어보기"
                        />
                      </div>
                    </div>
                  )}

                  {/* View More Button */}
                  {processedItems.length > displayedItems.length && (
                    <div className="mt-6 text-center">
                      <Button
                        variant="outline"
                        size="lg"
                        data-testid="button-view-more"
                        onClick={() => setShowAll(true)}
                      >
                        더보기 ({processedItems.length - displayedItems.length}
                        개 더)
                      </Button>
                    </div>
                  )}
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
